import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Filter } from 'lucide-react';
import {
  dismissAlert,
  fetchAlerts,
  markAlertRead,
  markAllAlertsRead,
} from '../../utils/adminAlerts';
import { useAdminNotifications } from '../../context/AdminNotificationContext';

const CATEGORIES = [
  { id: '', label: 'All' },
  { id: 'support', label: 'Support' },
  { id: 'monitor', label: 'Monitor' },
  { id: 'booking', label: 'Bookings' },
  { id: 'wallet', label: 'Wallet' },
  { id: 'refund', label: 'Refunds' },
  { id: 'credit', label: 'Credit' },
  { id: 'agent', label: 'Agents' },
];

export default function AdminAlertsInbox() {
  const navigate = useNavigate();
  const { refreshCount } = useAdminNotifications();
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [category, setCategory] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAlerts({
        category: category || null,
        unreadOnly,
        limit: 50,
      });
      setAlerts(data.alerts || []);
      setTotal(data.total || 0);
    } catch (_) {
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [category, unreadOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const openAlert = async (a) => {
    if (!a.read_at) {
      await markAlertRead(a.id).catch(() => {});
      refreshCount();
    }
    if (a.action_url) navigate(a.action_url);
    load();
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Alerts inbox</h1>
            <p className="text-sm text-gray-500">{total} notification{total !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={async () => {
            await markAllAlertsRead();
            refreshCount();
            load();
          }}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
        >
          <CheckCheck className="w-4 h-4" />
          Mark all read
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {CATEGORIES.map((c) => (
          <button
            key={c.id || 'all'}
            type="button"
            onClick={() => setCategory(c.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              category === c.id ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {c.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setUnreadOnly((v) => !v)}
          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
            unreadOnly ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
          }`}
        >
          <Filter className="w-3.5 h-3.5" />
          Unread only
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-gray-500 text-sm">Loading…</p>
        ) : alerts.length === 0 ? (
          <p className="p-8 text-center text-gray-500 text-sm">No alerts match your filters.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {alerts.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => openAlert(a)}
                  className={`w-full text-left px-4 py-4 hover:bg-gray-50 transition-colors ${
                    !a.read_at ? 'bg-red-50/30' : ''
                  }`}
                >
                  <div className="flex justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-900">{a.title}</p>
                      <p className="text-sm text-gray-600 mt-0.5">{a.body}</p>
                      <p className="text-xs text-gray-400 mt-1 capitalize">
                        {a.category} · {a.severity}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">
                      {new Date(a.created_at).toLocaleString()}
                    </span>
                  </div>
                </button>
                <div className="px-4 pb-2 flex gap-2">
                  {!a.read_at && (
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:underline"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await markAlertRead(a.id);
                        refreshCount();
                        load();
                      }}
                    >
                      Mark read
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-xs text-gray-500 hover:underline"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await dismissAlert(a.id);
                      refreshCount();
                      load();
                    }}
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
